/**
 * DeadlineForm Component
 *
 * Form for creating and editing deadlines with all fields including
 * alerts configuration and recurrence settings.
 */

import { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import {
  DeadlineFormData,
  DeadlineType,
  DeadlinePriority,
  AlertChannel,
  RecurrencePattern,
  DEADLINE_TYPES,
  DEADLINE_PRIORITIES,
  ALERT_CHANNELS,
  RECURRENCE_PATTERNS,
} from './types';

interface DeadlineFormProps {
  initialData?: Partial<DeadlineFormData>;
  onSubmit: (data: DeadlineFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  isLoading?: boolean;
  documents?: Array<{ documentId: string; fileName: string }>;
}

export function DeadlineForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Create Deadline',
  isLoading = false,
  documents = [],
}: DeadlineFormProps): JSX.Element {
  const [title, setTitle] = useState(initialData?.title || '');
  const [dueDate, setDueDate] = useState(initialData?.dueDate || '');
  const [type, setType] = useState<DeadlineType>(initialData?.type || 'other');
  const [priority, setPriority] = useState<DeadlinePriority>(initialData?.priority || 'medium');
  const [description, setDescription] = useState(initialData?.description || '');
  const [relatedDocumentId, setRelatedDocumentId] = useState(initialData?.relatedDocumentId || '');
  const [isRecurring, setIsRecurring] = useState(initialData?.isRecurring || false);
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern | undefined>(
    initialData?.recurrencePattern
  );
  const [alerts, setAlerts] = useState<Array<{
    daysBeforeDeadline: number;
    channels: AlertChannel[];
    enabled: boolean;
  }>>(initialData?.alerts || [{ daysBeforeDeadline: 7, channels: ['push'], enabled: true }]);

  const [error, setError] = useState<string | null>(null);

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDueDate(initialData.dueDate || '');
      setType(initialData.type || 'other');
      setPriority(initialData.priority || 'medium');
      setDescription(initialData.description || '');
      setRelatedDocumentId(initialData.relatedDocumentId || '');
      setIsRecurring(initialData.isRecurring || false);
      setRecurrencePattern(initialData.recurrencePattern);
      setAlerts(initialData.alerts || [{ daysBeforeDeadline: 7, channels: ['push'], enabled: true }]);
    }
  }, [initialData]);

  const handleAddAlert = () => {
    setAlerts([...alerts, { daysBeforeDeadline: 1, channels: ['push'], enabled: true }]);
  };

  const handleRemoveAlert = (index: number) => {
    setAlerts(alerts.filter((_, i) => i !== index));
  };

  const handleAlertDaysChange = (index: number, days: number) => {
    const newAlerts = [...alerts];
    newAlerts[index].daysBeforeDeadline = days;
    setAlerts(newAlerts);
  };

  const handleAlertChannelToggle = (index: number, channel: AlertChannel) => {
    const newAlerts = [...alerts];
    const channels = newAlerts[index].channels;
    if (channels.includes(channel)) {
      newAlerts[index].channels = channels.filter((c) => c !== channel);
    } else {
      newAlerts[index].channels = [...channels, channel];
    }
    setAlerts(newAlerts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }
    if (trimmedTitle.length > 200) {
      setError('Title cannot exceed 200 characters');
      return;
    }
    if (!dueDate) {
      setError('Due date is required');
      return;
    }
    if (isRecurring && !recurrencePattern) {
      setError('Please select a recurrence pattern');
      return;
    }

    // Filter out alerts with no channels
    const validAlerts = alerts.filter((alert) => alert.channels.length > 0);

    try {
      await onSubmit({
        title: trimmedTitle,
        dueDate,
        type,
        priority,
        description: description.trim() || undefined,
        relatedDocumentId: relatedDocumentId || undefined,
        isRecurring,
        recurrencePattern: isRecurring ? recurrencePattern : undefined,
        alerts: validAlerts,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save deadline');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error display */}
      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="label" htmlFor="deadline-title">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          id="deadline-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input w-full"
          placeholder="e.g., IEP Annual Review"
          maxLength={200}
          disabled={isLoading}
        />
        <p className="helper-text">{title.length}/200 characters</p>
      </div>

      {/* Due Date */}
      <div>
        <label className="label" htmlFor="deadline-due-date">
          Due Date <span className="text-red-500">*</span>
        </label>
        <input
          id="deadline-due-date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="input w-full"
          disabled={isLoading}
        />
      </div>

      {/* Type and Priority row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Type */}
        <div>
          <label className="label" htmlFor="deadline-type">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            id="deadline-type"
            value={type}
            onChange={(e) => setType(e.target.value as DeadlineType)}
            className="input w-full"
            disabled={isLoading}
          >
            {DEADLINE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Priority */}
        <div>
          <label className="label" htmlFor="deadline-priority">
            Priority <span className="text-red-500">*</span>
          </label>
          <select
            id="deadline-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as DeadlinePriority)}
            className="input w-full"
            disabled={isLoading}
          >
            {DEADLINE_PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="label" htmlFor="deadline-description">
          Description
        </label>
        <textarea
          id="deadline-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input w-full min-h-[100px]"
          placeholder="Additional details about this deadline..."
          maxLength={2000}
          disabled={isLoading}
        />
        <p className="helper-text">{description.length}/2000 characters</p>
      </div>

      {/* Related Document */}
      {documents.length > 0 && (
        <div>
          <label className="label" htmlFor="deadline-document">
            Related Document
          </label>
          <select
            id="deadline-document"
            value={relatedDocumentId}
            onChange={(e) => setRelatedDocumentId(e.target.value)}
            className="input w-full"
            disabled={isLoading}
          >
            <option value="">None</option>
            {documents.map((doc) => (
              <option key={doc.documentId} value={doc.documentId}>
                {doc.fileName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Recurrence */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            className="w-4 h-4 text-evergreen border-gray-300 rounded focus:ring-evergreen"
            disabled={isLoading}
          />
          <span className="text-sm font-medium text-ink">This is a recurring deadline</span>
        </label>

        {isRecurring && (
          <div className="pl-6">
            <label className="label" htmlFor="deadline-recurrence">
              Repeat Every
            </label>
            <select
              id="deadline-recurrence"
              value={recurrencePattern || ''}
              onChange={(e) => setRecurrencePattern(e.target.value as RecurrencePattern)}
              className="input w-full"
              disabled={isLoading}
            >
              <option value="">Select pattern...</option>
              {RECURRENCE_PATTERNS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Alerts Configuration */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="label mb-0">Reminders</label>
          <button
            type="button"
            onClick={handleAddAlert}
            className="btn-ghost btn-sm flex items-center gap-1"
            disabled={isLoading}
          >
            <Plus size={14} />
            Add Reminder
          </button>
        </div>

        {alerts.map((alert, index) => (
          <div key={index} className="p-3 bg-soft-neutral rounded-lg space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="365"
                  aria-label={`Reminder ${index + 1}: days before deadline`}
                  value={alert.daysBeforeDeadline}
                  onChange={(e) => handleAlertDaysChange(index, parseInt(e.target.value) || 0)}
                  className="input w-20 text-center"
                  disabled={isLoading}
                />
                <span className="text-sm text-muted">days before</span>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAlert(index)}
                className="btn-ghost btn-sm p-1 text-red-500 hover:bg-red-50"
                disabled={isLoading}
              >
                <Trash2 size={14} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {ALERT_CHANNELS.map((channel) => (
                <label key={channel.value} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={alert.channels.includes(channel.value)}
                    onChange={() => handleAlertChannelToggle(index, channel.value)}
                    className="w-3.5 h-3.5 text-evergreen border-gray-300 rounded focus:ring-evergreen"
                    disabled={isLoading}
                  />
                  <span className="text-xs text-muted">{channel.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {alerts.length === 0 && (
          <p className="text-sm text-muted italic">No reminders configured</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-soft-neutral">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex-1"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary flex-1 flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading && <Loader2 size={16} className="animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export default DeadlineForm;
